import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, Building, FileText, InfoIcon, PlusCircle } from 'lucide-react'
import { useEffect, useRef } from 'react'
import {
  getMarketResearchReportsAppMarketResearchReportsGetOptions,
} from '@/client/@tanstack/react-query.gen'
import { EntitlementButton } from '@/components/common/feature-gating/entitlement-button'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useAnalytics } from '@/hooks/use-analytics'
import { useAuth } from '@/hooks/use-auth'
import { useAutoTour } from '@/hooks/use-tour'
import { CreateReportDialog } from './-components/create-report-dialog'
import { MarketResearchCard } from './-components/market-research-card'
import { MarketResearchSkeleton } from './-components/market-research.skeleton'

export const Route = createFileRoute('/_authed/_user/market-research/')({
  component: MarketResearchReports,
  pendingComponent: MarketResearchSkeleton,
})

function MarketResearchReports() {
  const { companyInfo, canUseMarketResearch, isDistributorOrg } = useAuth()
  const { capture } = useAnalytics()
  const listViewedRef = useRef(false)

  // Polls every 10s while any report is generating; refetches on mount (no staleTime)
  const { data: reports, isLoading, isError } = useQuery({
    ...getMarketResearchReportsAppMarketResearchReportsGetOptions(),
    refetchInterval: (query) => {
      const hasGenerating = query.state.data?.some(r => !r.processingStatus) ?? false
      return hasGenerating ? 10_000 : false
    },
  })

  useEffect(() => {
    if (isLoading || isError || listViewedRef.current)
      return
    listViewedRef.current = true
    capture('market_research_list_viewed', { report_count: reports?.length ?? 0 })
  }, [capture, isLoading, isError, reports])

  // Gate the tour on a state where the Create Report anchor actually mounts:
  // not loading, not errored, company profile present, and the org can use
  // market research. Without this, the welcome step can fire over a skeleton
  // / entitlement-gated page and the user's "Got it" click sets the seen-key,
  // permanently consuming the tour without ever teaching the real UI.
  const tourReady = !isLoading && !isError && !!companyInfo && canUseMarketResearch
  useAutoTour('market-research-intro', tourReady)

  if (isLoading) {
    return <MarketResearchSkeleton />
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-1">Market Research Reports</h1>
          <p className="text-sm text-muted-foreground">
            AI-powered industry and market analysis
          </p>
        </div>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="max-w-md mx-auto">
              <AlertCircle className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                Unable to load reports
              </h3>
              <p className="text-sm text-muted-foreground">
                Market research reports require an organization. Please contact your administrator if you believe this is an error.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const hasReports = reports && reports.length > 0

  return (
    <div className="space-y-6">
      {isDistributorOrg && !canUseMarketResearch && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex items-center gap-2">
            <InfoIcon className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              {'Market Research Reports require a paid plan. Contact '}
              <a
                href="mailto:support@supplyco.ai"
                className="font-medium underline hover:text-amber-900"
              >
                support@supplyco.ai
              </a>
              {' to upgrade.'}
            </p>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-1">Market Research Reports</h1>
          <p className="text-sm text-muted-foreground">
            Research any market or buyer profile with AI-generated reports covering market size, growth trends, key players, and opportunities.
          </p>
        </div>
        {hasReports && companyInfo && (
          canUseMarketResearch
            ? (
                <CreateReportDialog>
                  <Button data-tour="market-research-create-btn">
                    <PlusCircle className="h-4 w-4" />
                    Create Report
                  </Button>
                </CreateReportDialog>
              )
            : (
                <EntitlementButton entitlement="canUseMarketResearch">
                  <PlusCircle className="h-4 w-4" />
                  Create Report
                </EntitlementButton>
              )
        )}
      </div>

      {companyInfo
        ? (
            <div data-tour="market-research-list" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {hasReports
                ? (
                    reports.map(report => (
                      <MarketResearchCard key={report.id} report={report} />
                    ))
                  )
                : (
                    <div className="col-span-3">
                      <Card>
                        <CardContent className="pt-6 text-center">
                          <div className="max-w-md mx-auto">
                            <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                            <h3 className="text-lg font-medium mb-2">
                              No market research reports yet
                            </h3>
                            <p className="text-sm text-muted-foreground mb-4">
                              Create your first market research report to get insights on industries and markets.
                            </p>
                            {canUseMarketResearch
                              ? (
                                  <CreateReportDialog>
                                    <Button data-tour="market-research-create-btn">
                                      <PlusCircle className="h-4 w-4" />
                                      Create Your First Report
                                    </Button>
                                  </CreateReportDialog>
                                )
                              : (
                                  <EntitlementButton entitlement="canUseMarketResearch">
                                    <PlusCircle className="h-4 w-4" />
                                    Create Your First Report
                                  </EntitlementButton>
                                )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
            </div>
          )
        : (
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="max-w-md mx-auto">
                  <Building className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    Company information required
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Market research reports are customized to your business. Please complete your company profile to start generating reports.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {'If you believe this is an error, please contact '}
                    <a href="mailto:support@supplyco.ai" className="text-primary hover:underline">
                      support@supplyco.ai
                    </a>
                    {' for assistance.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
    </div>
  )
}
